import { toen_manager } from 'twrk/Toen.js';

import { asset } from './asset.js';

//drum roll for the swing build-up: starts once the swinger has picked up a bit of speed, loops
//for as long as it keeps swinging, and caps off with a hit the moment it lets go of the rope.
//The outcome plays a crowd reaction immediately — cheer on a catch, ouch on a fall. The drum cycle
//is speed-driven: after the reaction it's ready again and re-rolls as soon as the swinger's speed
//climbs back over SPEED_MIN.
export const SPEED_MIN = 0.6; //world units/s of torso speed needed to (re)start the drum roll

export const Toen = toen_manager();

//several ouch takes, cycled through on each fall so the crowd never sounds identical twice running
const OUCH_VARIANTS = ["crowd_ouch1", "crowd_ouch2", "crowd_ouch3", "crowd_ouch4"];

type roll_state = "idle" | "starting" | "looping" | "ended";
let state: roll_state = "idle";

export async function sound_load() {
    await Toen.preload([
        { id: "drum_start", file: asset("drum_start.mp3"), on_end: () => { if (state === "starting") { state = "looping"; Toen.play("drum_loop"); } } },
        { id: "drum_loop", file: asset("drum_loop.mp3"), loop: true },
        { id: "drum_end", file: asset("drum_end.mp3") },
        { id: "crowd_cheer", file: asset("crowd_cheer.mp3") },
        ...OUCH_VARIANTS.map(id => ({ id, file: asset(`${id}.mp3`), volume: 0.5 })),
        { id: "background_loop", file: asset("background_loop.mp3"), loop: true, volume: 0.15 },
        { id: "crowd_applause", file: asset("crowd_applause.mp3") },
    ]);
}

//quiet endless bed music, kicked off a few seconds after the trigger so it eases in behind the
//moment rather than landing on top of it. on_play fires the moment the loop actually starts, which
//is the show's finale cue (end title).
export function music_start(on_play?: () => void) {
    setTimeout(() => {
        Toen.play("background_loop");
        on_play?.();
    }, 4000);
}

//applause greeting the audience the moment the show starts
export function show_start() {
    Toen.play("crowd_applause");
}

//speed-driven start/restart: rolls whenever the swinger is above SPEED_MIN and the drum isn't
//already mid-roll or ended-and-waiting-for-its-outcome. "idle" is the only state that (re)starts.
export function drumroll_update(speed: number) {
    if (state === "idle" && speed > SPEED_MIN) {
        state = "starting";
        Toen.play("drum_start");
    }
}

export function drumroll_release() {
    if (state === "idle" || state === "ended") return;
    state = "ended";
    Toen.get("drum_start").stop();
    Toen.get("drum_loop").stop();
    Toen.play("drum_end");
}

//outcome of the swing: play the crowd reaction immediately, silence any still-running roll and its
//end hit so the reaction lands clean, and return the drum to idle so the speed gate can roll again.
const crowd = (id: string) => {
    Toen.get("drum_start").stop();
    Toen.get("drum_loop").stop();
    Toen.get("drum_end").stop();
    state = "idle";
    Toen.play(id);
};

export const crowd_cheer = () => crowd("crowd_cheer");

//cycle through the ouch variants so consecutive falls don't sound identical
let ouch_i = 0;
export const crowd_ouch = () => {
    crowd(OUCH_VARIANTS[ouch_i]);
    ouch_i = (ouch_i + 1) % OUCH_VARIANTS.length;
};
